import { useEffect } from 'react'
import { useCallback } from 'react'
import { useRef } from 'react'
import { useState } from 'react'
import { memo } from 'react'
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect'
import ReactPlayer from 'react-player'
import { useRecoilState } from 'recoil'
import ContentLoader from 'react-content-loader'
import useSaveLastPlayedTime from '@/components/AnnotationsView/useSaveLastPlayedTime'
import {
  videoProgressState,
  currentAnnotationState,
  ocrState,
  seekToState,
  isPlayingState,
  videoAnnotationsState,
  useIsRowView,
} from '@/store'
import ResizeHandle from '@/components/ResizeHandle'
import analytics from '@/utils/analytics'
import Image from 'next/image'
import { useHotKeys } from '@/utils/shortcuts'
import toast from 'react-hot-toast'
import { Bookmark } from 'react-feather'
import ReactTooltip from 'react-tooltip'

const sizeStorageKey = 'player-size'
const sizeWidthStorageKey = 'player-size-width'

export default function Player({
  video,
  isDragging,
  setIsDragging,
  isHovering,
  setIsHovering,
  isDemo = false,
}) {
  const [ocr] = useRecoilState(ocrState)
  const [seekTo, setSeekTo] = useRecoilState(seekToState)
  const [isLoading, setIsLoading] = useState(true)
  const [currentAnnotation] = useRecoilState(currentAnnotationState)
  const [, setProgress] = useRecoilState(videoProgressState)
  const [isPlaying, setIsPlaying] = useRecoilState(isPlayingState)
  useSaveLastPlayedTime({ video, isDemo })
  const playerRef = useRef()
  const [playerHeight, setPlayerHeight] = useState(
    window.localStorage.getItem(sizeStorageKey) || '50%'
  )
  const [playerWidth, setPlayerWidth] = useState(
    window.localStorage.getItem(sizeWidthStorageKey) || '50%'
  )
  const [playbackRate] = useShortctus()
  const showDragging = isHovering || isDragging
  const isRowView = useIsRowView()

  useEffect(() => {
    setIsPlaying(isDemo)
  }, [video._id, isDemo, setIsPlaying])

  useEffect(() => {
    if (!seekTo || isLoading) {
      return
    }

    const { time, shouldPlay } = seekTo

    if (isPlaying) {
      playerRef.current.seekTo(time)
      setSeekTo(null)
    } else if (shouldPlay) {
      setIsPlaying(true)
      playerRef.current.seekTo(time)
      setSeekTo(null)
    }
  }, [seekTo, isLoading, isPlaying, setIsPlaying, setSeekTo])

  useDeepCompareEffectNoCheck(() => {
    if (playerRef.current && currentAnnotation) {
      if (!isPlaying) {
        setIsPlaying(true)
      }
      if (currentAnnotation.shouldSeek !== false) {
        playerRef.current.seekTo(currentAnnotation.start)
      }
    }
  }, [currentAnnotation])

  const style = isRowView
    ? {
        height: playerHeight,
        maxHeight: '70vh',
        minHeight: '20vh',
      }
    : {
        height: '100vh',
        width: playerWidth,
        maxWidth: '70vw',
        minWidth: '20vw',
      }

  const dragginCursor = isRowView ? 'row-resize' : 'col-resize'

  return (
    <div
      className="flex flex-col h-0 z-40 relative"
      style={{
        cursor: isDragging ? dragginCursor : 'default',
        ...style,
      }}
    >
      {isLoading && (
        <div className="center w-full h-full">
          <div className="absolute w-14">
            <Image
              src="/img/logos/logo-footer.svg"
              alt="logo"
              width={50}
              height={50}
            />
          </div>
          <ContentLoader
            speed={2}
            width="100%"
            height="300px"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            backgroundColor="#e8e7ee"
            foregroundColor="#f5f0f0"
            className="h-full"
          >
            <rect x="0" y="1" rx="3" ry="3" width="100" height="100" />
          </ContentLoader>
        </div>
      )}

      <ReactPlayer
        ref={playerRef}
        url={video.url}
        controls={true}
        style={ocr ? { position: 'relative', zIndex: '999999999' } : {}}
        width={isLoading ? 0 : '100%'}
        height={isLoading ? 0 : '100%'}
        playing={isPlaying}
        onReady={() => setIsLoading(false)}
        onProgress={setProgress}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        volume={isDemo ? 0 : 5}
        muted={isDemo}
        playbackRate={playbackRate}
      />

      {!isLoading && isRowView && (
        <MarkersProgressBar videoDuration={video.duration} />
      )}

      <ResizeHandle
        styleProps={{
          position: 'absolute',
          zIndex: '9999999992',
          ...(isRowView
            ? {
                bottom: '-8px',
              }
            : {
                right: '-8px',
              }),
        }}
        direction={isRowView ? 'vertical' : 'horizontal'}
        isDragging={isDragging}
        onHandleMove={useCallback(
          (newSize) => {
            if (isRowView) {
              setPlayerHeight(newSize)
            } else {
              setPlayerWidth(newSize)
            }
            setIsDragging(true)
          },
          [setIsDragging, isRowView]
        )}
        onStartResizing={useCallback(() => {
          setIsDragging(true)
        }, [setIsDragging])}
        onStopResizing={useCallback(
          (newSize) => {
            setIsDragging(false)
            analytics.track('Resized video', { isRowView })
            if (isRowView) {
              window.localStorage.setItem(sizeStorageKey, `${newSize}px`)
            } else {
              window.localStorage.setItem(sizeWidthStorageKey, `${newSize}px`)
            }
          },
          [setIsDragging, isRowView]
        )}
        onStartHover={useCallback(() => setIsHovering(true), [setIsHovering])}
        onFinishHover={useCallback(() => setIsHovering(false), [setIsHovering])}
      />

      {showDragging && (
        <div className="absolute inset-0" style={{ zIndex: '9999999991' }} />
      )}
    </div>
  )
}

const MarkersProgressBar = memo(function MarkersProgressBar({ videoDuration }) {
  const [currentAnnotation, setCurrentAnnotation] = useRecoilState(
    currentAnnotationState
  )
  const [annotations] = useRecoilState(videoAnnotationsState)
  if (!annotations) {
    return null
  }

  const markerSize = 20

  function onClick(annotation) {
    setCurrentAnnotation(annotation)
    analytics.track('Clicked on marker')
  }

  // const annotations2 = [
  //   {
  //     ...annotations[0],
  //     start: 0,
  //   },
  //   {
  //     ...annotations[0],
  //     start: videoDuration / 2,
  //   },
  //   {
  //     ...annotations[0],
  //     start: videoDuration,
  //   },
  // ]

  return (
    <div className="absolute w-full" style={{ bottom: '-4px' }}>
      {annotations.map((annotation) => {
        const tooltipId = `${annotation._id}-marker-tooltip`
        const markerPosition = (annotation.start * 100) / videoDuration
        const deltaMarkerSize = markerSize - markerSize / 3
        const left = `calc(${markerPosition}% - ${deltaMarkerSize}px)`
        const isActive = currentAnnotation?._id === annotation._id

        return (
          <span key={tooltipId}>
            <Bookmark
              data-tip
              data-for={tooltipId}
              className={`absolute clickable ${
                isActive ? 'opacity-100' : 'opacity-50'
              } hover:opacity-100`}
              style={{
                stroke: 'none',
                fill: '#6381f3',
                width: `${markerSize}px`,
                height: `${markerSize}px`,
                left,
              }}
              onClick={() => onClick(annotation)}
            />
            <ReactTooltip
              id={tooltipId}
              place="bottom"
              effect="solid"
              className="truncate max-w-xs"
            >
              {annotation.title ? annotation.title : annotation.content}
            </ReactTooltip>
          </span>
        )
      })}
    </div>
  )
})

const defaultPlaybackRate = 1
const playbackRates = [0.25, 0.5, 0.75, defaultPlaybackRate, 1.25, 1.5, 1.75, 2]

function useShortctus() {
  const [playbackChanged, setPlaybackChanged] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(defaultPlaybackRate)
  const [, setIsPlaying] = useRecoilState(isPlayingState)
  const [progress] = useRecoilState(videoProgressState)
  const [, setSeekTo] = useRecoilState(seekToState)
  const secondsToChange = 10

  useEffect(() => {
    if (playbackChanged) {
      toast.success(`${playbackRate}x`)
    }
  }, [playbackRate, playbackChanged])

  // Play/Pause
  useHotKeys('space', () => setIsPlaying((isPlaying) => !isPlaying))
  useHotKeys('k', () => setIsPlaying((isPlaying) => !isPlaying))

  // Decrease playback rate
  // >
  useHotKeys('shift+.', () =>
    setPlaybackRate((playbackRate) => {
      setPlaybackChanged(true)
      const currentPlaybackRateIndex = playbackRates.indexOf(playbackRate)
      return playbackRates[currentPlaybackRateIndex - 1] || defaultPlaybackRate
    })
  )

  // Increase playback rate
  // <
  useHotKeys('shift+,', () =>
    setPlaybackRate((playbackRate) => {
      setPlaybackChanged(true)
      const currentPlaybackRateIndex = playbackRates.indexOf(playbackRate)
      return playbackRates[currentPlaybackRateIndex + 1] || defaultPlaybackRate
    })
  )

  // Move backward
  useHotKeys(
    'left,j',
    () => {
      setSeekTo({
        time: progress.playedSeconds - secondsToChange,
        shouldPlay: true,
      })
    },
    {},
    [progress]
  )

  // Move forward
  useHotKeys(
    'right,l',
    () => {
      setSeekTo({
        time: progress.playedSeconds + secondsToChange,
        shouldPlay: true,
      })
    },
    {},
    [progress]
  )

  return [playbackRate]
}
